import { World } from './world'
import { Enemy } from './types'
import { dist2 } from '../core/math'

export function nearestEnemy(world: World, x: number, z: number, maxRange = Infinity): Enemy | null {
  let best: Enemy | null = null
  let bestD = maxRange * maxRange
  for (const e of world.enemies) {
    if (!e.alive) continue
    const d = dist2(x, z, e.x, e.z)
    if (d < bestD) {
      bestD = d
      best = e
    }
  }
  return best
}

export function randomEnemy(world: World, x: number, z: number, maxRange = Infinity): Enemy | null {
  const r2 = maxRange * maxRange
  const pool: Enemy[] = []
  for (const e of world.enemies) {
    if (e.alive && dist2(x, z, e.x, e.z) <= r2) pool.push(e)
  }
  if (pool.length === 0) return null
  return pool[Math.floor(world.rng.next() * pool.length)]
}

export function enemiesInRadius(world: World, x: number, z: number, radius: number): Enemy[] {
  const r2 = radius * radius
  const out: Enemy[] = []
  for (const e of world.enemies) {
    if (e.alive && dist2(x, z, e.x, e.z) <= r2) out.push(e)
  }
  return out
}

/**
 * Marksman manual-aim: the in-range enemy most ALIGNED with the player's facing — i.e. you shoot where you point.
 * Score = squared distance penalised by angular deviation from `facing`, so a foe dead-ahead beats a closer one off
 * to the side. Falls back to nearest when nothing sits in the cone. Deterministic (facing comes from input/movement).
 */
export function enemyTowardFacing(world: World, x: number, z: number, facing: number, maxRange = Infinity): Enemy | null {
  let best: Enemy | null = null
  let bestScore = Infinity
  const r2 = maxRange * maxRange
  const fx = Math.cos(facing)
  const fz = Math.sin(facing)
  for (const e of world.enemies) {
    if (!e.alive) continue
    const dx = e.x - x
    const dz = e.z - z
    const d2 = dx * dx + dz * dz
    if (d2 > r2) continue
    const len = Math.sqrt(d2) || 1
    const dot = (dx * fx + dz * fz) / len // 1 = dead-ahead, -1 = behind
    // weight by alignment: behind-you targets are heavily penalised, ahead ones favoured
    const align = 1.6 - dot // 0.6 (ahead) .. 2.6 (behind)
    const score = d2 * align * align
    if (score < bestScore) {
      bestScore = score
      best = e
    }
  }
  return best
}

/** Strongest (highest current hp) enemy within range — good for boss-seeking weapons. */
export function toughestEnemy(world: World, x: number, z: number, maxRange = Infinity): Enemy | null {
  let best: Enemy | null = null
  let bestHp = 0
  const r2 = maxRange * maxRange
  for (const e of world.enemies) {
    if (!e.alive) continue
    if (dist2(x, z, e.x, e.z) > r2) continue
    if (e.hp > bestHp) {
      bestHp = e.hp
      best = e
    }
  }
  return best
}
